/**
 * DOC HELPER — WRK-04 canvas projection boundary.
 *
 * This hook maps timeline/backend payloads into canvas props and local editing
 * state. It does not persist context directly or decide backend workflow state.
 */
import React, { useMemo } from 'react';
import { useArtifactDraft } from '../../application/artifacts/provider';
import { selectProjectedCanvasEvidenceItems } from '../../projection/canvas/evidenceProjectionSelector.js';
import { resolveSavedSourcingEvidenceInput } from '../../projection/canvas/savedSourcingEvidenceProjection.js';
import { resolveRetrievalEvidenceInput } from '../../projection/canvas/savedRetrievalEvidenceProjection.js';
import {
  selectCanvasAnalysisPayload,
  selectCanvasAnalysisPending,
} from '../../projection/canvas/canvasAnalysisPayloadSelector.js';
import {
  selectCanvasEvidencePresence,
  selectCanvasTabs,
  selectResolvedCanvasEvidenceTabKey,
} from '../../projection/canvas/canvasTabsSelector.js';
import {
  selectCanvasStatePresence,
  selectLatestCanvasSignal,
} from '../../projection/canvas/canvasSignalSelector.js';
import { selectCanvasEvidenceSummary } from '../../projection/canvas/canvasEvidenceSummarySelector.js';
import { selectCanvasFieldRows } from '../../projection/canvas/canvasFieldRowsSelector.js';
import { selectCurrentIntakeCanvasProjection } from '../../projection/canvas/currentArtifactProjectionSelector.js';
import { selectRenderedTimelineItems } from '../../projection/timeline/assistantTextSelector.js';
import { selectCanvasReportTemplate } from '../../projection/canvas/canvasReportTemplateSelector.js';
import { selectCanvasWorkflowProjection } from '../../projection/canvas/canvasWorkflowProjectionSelector.js';
import { selectArtifactContentForTab } from '../../projection/canvas/artifactDocumentView.js';
import {
  selectArtifactRevisionControls,
  selectArtifactRevisionEntries,
} from '../../projection/canvas/artifactRevisionView.js';
import { useCanvasDocumentSaveState } from '../../application/artifacts/document-save-state.js';
import { useCanvasAutoTabEffect } from './projection-auto-tab-effect.js';
import { useCanvasProjectionDebug } from './projection-debug-effect.js';
import { useCanvasSignalEffect } from './projection-signal-effect.js';
import { useCanvasDraftActions } from '../../application/artifacts/draft-actions.js';
import { useCanvasInlineEditEffect } from '../../application/artifacts/inline-edit-effect.js';
import { useCanvasRevisionApplyAction } from '../../application/artifacts/revision-apply-action.js';
import { withSemanticMapPending } from './semantic-map-pending.js';
import { useCanvasContentSettling } from './content-settle-state.js';
import { resolveCanvasManualControlsEnabled } from './manual-controls-state.js';
import { buildCanvasDevDiagnostics } from './dev-diagnostics.js';

const everyTruthy = (...readers) => readers.reduce((result, read) => result && read(), true);
const firstTruthy = (...readers) => readers.reduce((result, read) => result || read(), undefined);

export function useProjection(options) {
  const { artifactReadState, timelineItems, composerModeKey, evidenceStrictMode, evidenceHitScoreThreshold, showCanvasPanel, canvasFocusEvidenceAnchorId, sessionContext, setCanvasActiveTabKey, setShowCanvasPanel, canvasActiveTabKey, canvasFullscreenOpen, setCanvasFullscreenOpen, inputState, themeTokens, sourcingHasMore, sourcingLoading, sourcingLoadingMore, semanticMapRefreshPending, onCanvasSourcingLoadMore, onCanvasDraftSaved } = { sourcingHasMore: false, sourcingLoading: false, sourcingLoadingMore: false, semanticMapRefreshPending: false, ...Object.fromEntries(Object.entries(Object(options)).filter(([, value]) => value !== undefined)) };
  const latestIntakeCanvasState = useMemo(() => selectCurrentIntakeCanvasProjection({
    sessionContext,
    timelineItems,
    canvasActiveTabKey,
  }), [sessionContext, timelineItems, canvasActiveTabKey]);
  const resolvedIntakeCanvasState = latestIntakeCanvasState;
  const artifactDocument = useMemo(() => firstTruthy(() => resolvedIntakeCanvasState.artifact_document, () => {}), [resolvedIntakeCanvasState.artifact_document]);
  const evidenceInput = useMemo(() => resolveRetrievalEvidenceInput({
    baseInput: resolveSavedSourcingEvidenceInput({ artifactDocument,
      artifactTabProjection: sessionContext.artifact_tab_projection, canvasActiveTabKey, timelineItems }),
    artifactDocument,
    artifactTabProjection: sessionContext.artifact_tab_projection,
    canvasActiveTabKey,
    timelineItems,
  }), [artifactDocument, canvasActiveTabKey, sessionContext.artifact_tab_projection, timelineItems]);
  const canvasEvidenceItems = useMemo(() => selectProjectedCanvasEvidenceItems({
    timelineItems: evidenceInput.timelineItems,
    artifactDocument: evidenceInput.artifactDocument,
    readingTargets: evidenceInput.readingTargets,
    evidenceReading: sessionContext.evidence_reading,
    composerModeKey,
    evidenceStrictMode,
    evidenceHitScoreThreshold,
  }), [composerModeKey, evidenceHitScoreThreshold, evidenceStrictMode, evidenceInput, sessionContext.evidence_reading]);
  const canvasReportTemplate = useMemo(
    () => selectCanvasReportTemplate(timelineItems),
    [timelineItems],
  );
  const canvasWorkflowProjection = useMemo(
    () => selectCanvasWorkflowProjection(sessionContext.workflow_projection),
    [sessionContext.workflow_projection],
  );
  const canvasAnalysisPayload = useMemo(
    () => selectCanvasAnalysisPayload({ sessionContext }),
    [sessionContext],
  );
  const { hasSourcingEvidenceRows, hasRetrievalEvidenceRows } = useMemo(
    () => selectCanvasEvidencePresence(canvasEvidenceItems),
    [canvasEvidenceItems],
  );
  const canvasStatePresence = useMemo(
    () => selectCanvasStatePresence(timelineItems),
    [timelineItems],
  );
  const canvasAnalysisPending = useMemo(
    () => selectCanvasAnalysisPending({ sessionContext }),
    [sessionContext],
  );
  const visibleCanvasAnalysisPayload = everyTruthy(() => canvasAnalysisPending, () => !canvasAnalysisPayload) ? null : canvasAnalysisPayload;

  useCanvasProjectionDebug({
    canvasEvidenceItems,
    effectiveCanvasReportTemplate: canvasReportTemplate,
    timelineItems,
    visibleCanvasAnalysisPayload,
  });

  const { canvasDraftEditing, setCanvasDraftEditing, canvasDraftEditorValue, setCanvasDraftEditorValue } = useArtifactDraft(canvasActiveTabKey);
  const canvasRequirementTabKey = 'requirement';
  const canvasAnalysisTabKey = 'analysis';
  const authoritativeIntakeContent = String(selectCurrentIntakeCanvasProjection({
    sessionContext, canvasActiveTabKey: canvasRequirementTabKey,
  }).content ?? '');
  const {
    resolvedAnalysisContent,
    resolvedAnalysisPayload: resolvedCanvasAnalysisPayload,
    resolvedIntakeContent,
  } = useCanvasDocumentSaveState({
    activeTabKey: canvasActiveTabKey,
    analysisPayload: visibleCanvasAnalysisPayload,
    analysisTabKey: canvasAnalysisTabKey,
    authoritativeIntakeContent,
    canvasDraftEditing,
    currentDraftContent: canvasDraftEditorValue,
    onHydrateDraftEditorValue: setCanvasDraftEditorValue,
    requirementTabKey: canvasRequirementTabKey,
  });
  const canonicalActiveArtifactContent = selectArtifactContentForTab({
    artifactDocument,
    fallbackContent: resolvedIntakeContent,
    tabKey: canvasActiveTabKey,
    versions: resolvedIntakeCanvasState.versions,
  });
  const canvasRevisionEntries = useMemo(() => selectArtifactRevisionEntries({
    artifactDocument,
    tabKey: canvasActiveTabKey,
    versions: resolvedIntakeCanvasState.versions,
  }), [
    artifactDocument,
    canvasActiveTabKey,
    resolvedIntakeCanvasState.versions,
  ]);
  const canvasRevisionControls = useMemo(() => selectArtifactRevisionControls({
    revisionControls: resolvedIntakeCanvasState.revision_controls,
    revisionEntries: canvasRevisionEntries,
  }), [
    canvasRevisionEntries,
    resolvedIntakeCanvasState.revision_controls,
  ]);
  const resolvedActiveIntakeContent = canonicalActiveArtifactContent;
  const canvasDisplayLines = resolvedActiveIntakeContent ? resolvedActiveIntakeContent.split('\n') : [];
  const canvasCollectedCount = Array.isArray(Object(resolvedIntakeCanvasState).collected)
    ? resolvedIntakeCanvasState.collected.length
    : 0;
  const canvasProgressPercent = Math.max(0, Math.min(100, Math.round(Number(firstTruthy(() => Object(resolvedIntakeCanvasState).progress, () => 0)) * 100)));

  const latestCanvasSignal = useMemo(
    () => selectLatestCanvasSignal(timelineItems, { artifactTabProjection: sessionContext.artifact_tab_projection }),
    [timelineItems, sessionContext.artifact_tab_projection],
  );
  const lastCanvasSignalKeyRef = React.useRef('');

  const canvasTabs = useMemo(() => selectCanvasTabs({
    artifactTabProjection: sessionContext.artifact_tab_projection,
  }), [sessionContext.artifact_tab_projection]);
  const resolvedCanvasTabs = useMemo(
    () => withSemanticMapPending(canvasTabs, semanticMapRefreshPending),
    [canvasTabs, semanticMapRefreshPending],
  );

  useCanvasSignalEffect({
    canvasTabs: resolvedCanvasTabs,
    latestCanvasSignal,
    lastCanvasSignalKeyRef,
    setCanvasActiveTabKey,
    setCanvasFullscreenOpen,
    setShowCanvasPanel,
  });

  useCanvasAutoTabEffect({
    artifactTabProjection: sessionContext.artifact_tab_projection,
    canvasActiveTabKey,
    canvasEvidenceItems,
    canvasFocusEvidenceAnchorId,
    canvasTabs: resolvedCanvasTabs,
    hasCanvasAnalysisPayload: Boolean(visibleCanvasAnalysisPayload),
    hasRetrievalEvidenceRows,
    hasSourcingCanvasState: canvasStatePresence.has_sourcing_canvas_state,
    setCanvasActiveTabKey,
    showCanvasPanel,
  });
  const resolvedCanvasEvidenceTabKey = useMemo(() => evidenceInput.evidenceTabKey ?? selectResolvedCanvasEvidenceTabKey({
    hasSourcingCanvasState: canvasStatePresence.has_sourcing_canvas_state,
    hasSourcingEvidenceRows,
    hasRetrievalEvidenceRows,
    canvasActiveTabKey,
  }), [canvasActiveTabKey, canvasStatePresence.has_sourcing_canvas_state, evidenceInput.evidenceTabKey, hasRetrievalEvidenceRows, hasSourcingEvidenceRows]);
  const canvasEvidenceSummary = useMemo(
    () => selectCanvasEvidenceSummary(canvasEvidenceItems, evidenceInput.timelineItems),
    [canvasEvidenceItems, evidenceInput.timelineItems],
  );
  const canvasManualControlsEnabled = resolveCanvasManualControlsEnabled(resolvedCanvasTabs);
  const canvasFieldRows = useMemo(() => selectCanvasFieldRows(resolvedIntakeCanvasState), [resolvedIntakeCanvasState]);
  const isAnalysisTabActive = canvasActiveTabKey === canvasAnalysisTabKey;
  const activeCanvasDraftEditorValue = canvasDraftEditing ? canvasDraftEditorValue : '';
  const currentDraftBaselineContent = isAnalysisTabActive ? resolvedAnalysisContent : resolvedActiveIntakeContent;
  useCanvasInlineEditEffect({
    canvasActiveTabKey,
    canvasDraftEditing,
    canvasDraftEditorValue,
    canvasManualControlsEnabled,
    canvasTabs: resolvedCanvasTabs,
    currentContent: currentDraftBaselineContent,
    setCanvasDraftEditing,
    setCanvasDraftEditorValue,
    showCanvasPanel,
  });
  const {
    commitCanvasDraftValue,
    handleCanvasToolbarAction,
    markCanvasDraftSaved,
  } = useCanvasDraftActions({
    artifactDocument,
    canvasActiveTabKey,
    canvasAnalysisTabKey,
    canvasDraftEditing,
    canvasDraftEditorValue,
    canvasManualControlsEnabled,
    currentDraftBaselineContent,
    onCanvasDraftSaved,
    resolvedActiveIntakeContent,
    resolvedCanvasAnalysisPayload,
    resolvedIntakeCanvasState,
    setCanvasDraftEditing,
    setCanvasDraftEditorValue,
  });
  const handleCanvasApplyRevision = useCanvasRevisionApplyAction({
    canvasActiveTabKey,
    commitCanvasDraftValue,
    markCanvasDraftSaved,
    setCanvasActiveTabKey,
  });
  const canvasContentSettling = useCanvasContentSettling({
    activeTabKey: canvasActiveTabKey,
    analysisContent: resolvedAnalysisContent,
    analysisPending: canvasAnalysisPending,
    contentGenerating: firstTruthy(() => semanticMapRefreshPending, () => canvasAnalysisPending),
    intakeContent: resolvedActiveIntakeContent,
    showCanvasPanel,
    tabs: resolvedCanvasTabs,
  });

  const panelProps = {
    artifactReadState,
    activeCanvasDraftEditorValue,
    artifactDocument,
    canvasActiveTabKey,
    canvasAnalysisPending,
    canvasCollectedCount,
    canvasContentSettling,
    canvasDisplayLines,
    canvasDraftEditing,
    canvasDraftEditorValue,
    canvasEvidenceItems,
    canvasEvidenceSummary,
    canvasFieldRows,
    canvasFocusEvidenceAnchorId,
    canvasFullscreenOpen,
    canvasManualControlsEnabled,
    canvasProgressPercent,
    canvasReportTemplate,
    canvasRevisionControls,
    canvasRevisionEntries,
    canvasTabs: resolvedCanvasTabs,
    canvasWorkflowProjection,
    commitCanvasDraftValue,
    handleCanvasApplyRevision,
    handleCanvasToolbarAction,
    inputState,
    isAnalysisTabActive,
    markCanvasDraftSaved,
    onCanvasSourcingLoadMore,
    resolvedActiveIntakeContent,
    resolvedAnalysisContent,
    resolvedCanvasAnalysisPayload,
    resolvedCanvasEvidenceTabKey,
    setCanvasActiveTabKey,
    setCanvasDraftEditing,
    setCanvasDraftEditorValue,
    setCanvasFullscreenOpen,
    setShowCanvasPanel,
    showCanvasPanel,
    sourcingHasMore,
    sourcingLoading,
    sourcingLoadingMore,
    themeTokens,
    timelineItems,
  };

  const renderedTimelineItems = useMemo(() => selectRenderedTimelineItems({
    timelineItems,
  }), [timelineItems]);
  const canvasDevDiagnostics = buildCanvasDevDiagnostics({ activeTabKey: canvasActiveTabKey, artifactDocument, revisionControls: canvasRevisionControls, revisionEntries: canvasRevisionEntries, showCanvasPanel, tabs: resolvedCanvasTabs });

  return {
    panelProps,
    renderedTimelineItems,
    canvasEvidenceItems,
    canvasAvailable: resolvedCanvasTabs.length > 0 || ['pending', 'failed'].includes(artifactReadState?.status),
    canvasDevDiagnostics,
  };
}
export default useProjection;
